import { useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ApiError } from "@/lib/api/unwrap";
import { Button } from "@/components/ui/button";
import { DirectoryPanelShell } from "@/components/directory/DirectoryPanelShell";
import { toast } from "@/components/ui/sonner";

import { DirectoryFilters } from "./DirectoryFilters";
import { ExpertCard } from "./ExpertCard";
import {
  useAddFavorite,
  useDirectoryQuery,
  useDirectoryOrgTypesQuery,
  useDirectorySectorsQuery,
  useFavoritesQuery,
  useRemoveFavorite,
  useServiceRegionsQuery,
} from "../../api/hooks";
import type { DirectoryExpert, DirectoryFilters as Filters } from "../../types";
import { EmptyState, ErrorState, Loading } from "../ui/states";

/** Experts shown before "see more" reveals the tail (matches the legacy cap). */
const INITIAL_COUNT = 10;

/** Relevance-tier dot colours — a teal→slate ramp kept off the thematic hues. */
const TIER_DOT = [
  "hsl(189 85% 34%)",
  "hsl(189 46% 52%)",
  "hsl(214 16% 56%)",
  "hsl(214 14% 74%)",
];

function tierColor(rank: number | null | undefined): string {
  return TIER_DOT[rank ?? 3] ?? "hsl(214 14% 74%)";
}

function isApiError(err: unknown): err is ApiError {
  return typeof err === "object" && err !== null && "status" in err;
}

interface TierGroup {
  key: string;
  label: string;
  rank: number | null;
  experts: DirectoryExpert[];
}

/** Group a rank-ordered result list into consecutive relevance tiers. */
function groupByTier(experts: DirectoryExpert[]): TierGroup[] {
  const groups: TierGroup[] = [];
  for (const expert of experts) {
    const label = expert.relevance_tier || "";
    const rank = expert.relevance_rank ?? null;
    const key = String(rank ?? label);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.experts.push(expert);
    else groups.push({ key, label, rank, experts: [expert] });
  }
  return groups;
}

export function DirectoryView({
  action,
  favoritesDefault = false,
  variant = "page",
  panelFooterAction,
}: {
  action: string;
  favoritesDefault?: boolean;
  /**
   * "page" — the full-width route layout (filters in a sticky sidebar rail,
   * cards in a 2-3 column grid). "panel" — the in-context slide-over layout
   * (parity with the old app's répertoire drawer): filters behind a toolbar
   * disclosure with the ♥ beside it, cards stacked single-column, and the
   * result count in a footer next to the full-directory link.
   */
  variant?: "page" | "panel";
  /** Escape hatch rendered in the panel footer (ignored on the full page). */
  panelFooterAction?: ReactNode;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Filters>({});
  const [favoritesOnly, setFavoritesOnly] = useState(favoritesDefault);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const sectionRefs = useRef(new Map<string, HTMLElement | null>());

  const directory = useDirectoryQuery(action, filters);
  const regionsQuery = useServiceRegionsQuery();
  const sectorsQuery = useDirectorySectorsQuery();
  const orgTypesQuery = useDirectoryOrgTypesQuery();
  const favoritesQuery = useFavoritesQuery(action);
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  // expert_profile id → favorite id, so a card's toggle-off can delete the row.
  const favoriteIdByExpert = useMemo(() => {
    const map = new Map<string, string>();
    for (const fav of favoritesQuery.data ?? []) {
      map.set(fav.expert_profile, fav.id);
    }
    return map;
  }, [favoritesQuery.data]);

  const handleToggleFavorite = async (expert: DirectoryExpert) => {
    if (!action) return;
    setTogglingId(expert.id);
    try {
      if (expert.is_favorite) {
        const favId = favoriteIdByExpert.get(expert.id);
        if (favId) await removeFavorite.mutateAsync(favId);
      } else {
        await addFavorite.mutateAsync({ action, expertProfileId: expert.id });
      }
    } catch (err) {
      const detail = isApiError(err) ? err.detail : undefined;
      toast.error(detail ?? t("expert.error"));
    } finally {
      setTogglingId(null);
    }
  };

  if (directory.isPending) return <Loading />;

  if (directory.isError) {
    const status = isApiError(directory.error) ? directory.error.status : 0;
    // Directory-disabled and action-can't-have-experts both 404 (matching the
    // old app, which 404s for either) — show the "unavailable" copy. Missing/
    // invalid action is a 400. None of these are retryable; only surface a
    // retry for transient (5xx / network) failures.
    const message =
      status === 404
        ? t("expert.directory.unavailable")
        : isApiError(directory.error)
          ? directory.error.detail
          : undefined;
    const onRetry =
      status >= 400 && status < 500
        ? undefined
        : () => void directory.refetch();
    return <ErrorState message={message} onRetry={onRetry} />;
  }

  // The two identities the card shows, matched the way the Django rail's
  // `filter_search` matches them (`expert/public_filters.py`): three
  // independent case-insensitive substring tests, not one over the three
  // joined — a needle spanning two fields matches neither app. Client side,
  // off the payload already fetched, as the tool rail does it.
  const needle = search.trim().toLowerCase();
  const allExperts = needle
    ? directory.data.filter((e) =>
        [
          e.organization_name,
          e.contact_primary_first_name,
          e.contact_primary_last_name,
        ].some((field) => (field ?? "").toLowerCase().includes(needle)),
      )
    : directory.data;
  const experts = favoritesOnly
    ? allExperts.filter((e) => e.is_favorite)
    : allExperts;
  const groups = groupByTier(experts);

  // Global cap across tiers: reveal only the first INITIAL_COUNT until expanded.
  // Favorites-only lifts the cap entirely (matches the legacy applyFavOnly).
  let remaining = showAll || favoritesOnly ? Infinity : INITIAL_COUNT;
  const rendered = groups.map((g) => {
    const take = Math.max(0, Math.min(g.experts.length, remaining));
    remaining -= take;
    return { group: g, shown: g.experts.slice(0, take) };
  });
  const extraCount = favoritesOnly
    ? 0
    : experts.length - Math.min(experts.length, INITIAL_COUNT);

  const scrollToTier = (key: string) => {
    sectionRefs.current.get(key)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isPanel = variant === "panel";
  const hasActiveFilters = Object.keys(filters).length > 0 || search !== "";

  const countLabel = favoritesOnly
    ? t("expert.directory.favoriteCount", { count: experts.length })
    : t("expert.directory.resultCount", { count: experts.length });

  const setFavoritesOnlyAndReset = (value: boolean) => {
    setFavoritesOnly(value);
    setShowAll(false);
  };

  const filtersEl = regionsQuery.data ? (
    <DirectoryFilters
      regions={regionsQuery.data}
      sectors={sectorsQuery.data ?? []}
      orgTypes={orgTypesQuery.data ?? []}
      filters={filters}
      onChange={(f) => {
        setFilters(f);
        setShowAll(false);
      }}
      search={search}
      onSearch={(v) => {
        setSearch(v);
        setShowAll(false);
      }}
      favoriteCount={favoritesQuery.data?.length ?? 0}
      favoritesOnly={favoritesOnly}
      onFavoritesOnlyChange={setFavoritesOnlyAndReset}
      // In the panel the ♥ lives in the always-visible toolbar row, so the rail
      // must not render a second copy of the same control.
      showFavorites={!isPanel}
    />
  ) : null;

  // Cards stack single-column in the narrow panel; the full page uses a grid.
  const cardGridClass = isPanel
    ? "grid gap-4"
    : "grid gap-4 sm:grid-cols-2 xl:grid-cols-3";

  const resultsBody =
    experts.length === 0 ? (
      <EmptyState
        title={
          favoritesOnly
            ? t("expert.directory.noFavorites")
            : t("expert.directory.none")
        }
      >
        {favoritesOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFavoritesOnly(false);
              setShowAll(false);
            }}
          >
            {t("expert.directory.seeAll")}
          </Button>
        )}
      </EmptyState>
    ) : (
      <>
        {groups.length > 1 && (
          <nav
            className="flex flex-wrap gap-2"
            aria-label={t("expert.directory.tierNavLabel")}
          >
            {groups.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => scrollToTier(g.key)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:border-foreground hover:text-foreground"
              >
                <span
                  className="size-2.5 rounded-full"
                  style={{ backgroundColor: tierColor(g.rank) }}
                  aria-hidden
                />
                {g.label}
                <span className="text-xs font-bold text-muted-foreground/80">
                  {g.experts.length}
                </span>
              </button>
            ))}
          </nav>
        )}

        {rendered.map(({ group, shown }) =>
          shown.length === 0 ? null : (
            <section
              key={group.key}
              ref={(el) => sectionRefs.current.set(group.key, el)}
              className="scroll-mt-20 space-y-3"
            >
              {group.label && (
                <div className="flex items-center gap-3">
                  <span
                    className="size-2.5 rounded-full"
                    style={{ backgroundColor: tierColor(group.rank) }}
                    aria-hidden
                  />
                  <h3 className="text-sm font-bold">{group.label}</h3>
                  {group.rank === 0 && (
                    <span className="rounded bg-[hsl(189_68%_95%)] px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[hsl(189_85%_30%)]">
                      {t("expert.directory.bestMatch")}
                    </span>
                  )}
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs font-bold text-muted-foreground">
                    {group.experts.length}
                  </span>
                </div>
              )}
              <div className={cardGridClass}>
                {shown.map((expert) => (
                  <ExpertCard
                    key={expert.id}
                    expert={expert}
                    action={action}
                    onToggleFavorite={(e) => void handleToggleFavorite(e)}
                    isToggling={togglingId === expert.id}
                  />
                ))}
              </div>
            </section>
          ),
        )}

        {!showAll && extraCount > 0 && (
          <div className="pt-2 text-center">
            <Button
              variant="outline"
              onClick={() => setShowAll(true)}
              className="gap-2"
            >
              {t("expert.directory.seeMore", { count: extraCount })}
              <ChevronDown className="size-4" aria-hidden />
            </Button>
          </div>
        )}
      </>
    );

  // Panel chrome (in-context drawer): favourites lead the toolbar row, the
  // result count trails in the footer alongside the full-directory link.
  if (isPanel) {
    return (
      <DirectoryPanelShell
        intro={
          <p className="text-sm text-muted-foreground">
            {t("expert.directory.help")}
          </p>
        }
        filtersLabel={t("expert.filters.panelToggle")}
        filtersOpenByDefault={hasActiveFilters}
        filters={filtersEl}
        favorites={{
          count: favoritesQuery.data?.length ?? 0,
          on: favoritesOnly,
          onChange: setFavoritesOnlyAndReset,
          label: t("expert.filters.favoritesOnly"),
        }}
        countLabel={countLabel}
        footerAction={panelFooterAction}
      >
        {resultsBody}
      </DirectoryPanelShell>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold">{t("expert.directory.heading")}</h2>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t("expert.directory.help")}
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[17rem_1fr]">
        {/* Sticky and taller than the viewport, the rail only unpins at the bottom of
            its grid row — with several facets open, reaching the last groups meant
            scrolling the page past every result. Let the rail scroll on its own. */}
        <aside className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
          {filtersEl}
        </aside>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{countLabel}</p>
          {resultsBody}
        </div>
      </div>
    </div>
  );
}
